import { useQuery } from "@tanstack/react-query";
import useAuthHttp from "../../hooks/useAuthHttp";
import AnalysisContainer from "./AnalysisContainer";
import Container from "../../components/Container";
import { Card } from "primereact/card";
import moment from "moment";
import { useState } from "react";
import GroupMultiSelect from "../../components/GroupMultiSelect";
import SubGroupMultiSelect from "../../components/SubGroupMultiSelect";
import AssetsMultiSelect from "../../components/AssetsMultiSelect";
import { IAssetTypesResponse } from "../../models/asset";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Badge } from "primereact/badge";
import Currency from "../../components/Currency";
import PercentageText from "../../components/PercentageText";

interface AssetPayoutDetails {
    asset_user_id: string;
    asset_quantity: number;
    asset_average_price: number;
    asset_actual_price: number;
    asset_total_earnings: number;
    asset_percent_yield: number;
    asset_total_result: number;
    asset_percent_result: number;
    asset_buy_price: number;
}

interface PayoutAnalysis {
    user_id: string;
    asset_user_id: string;
    asset_id: string;
    asset_type: string;
    asset_sub_type: string;
    asset_name: string;
    asset_description: string;
    asset_payout_details: AssetPayoutDetails;
}

interface IEarningsResponse {
    payout_analysis: PayoutAnalysis[];
}

interface IEarningsPageFilters {
    group: string[];
    subGroup: string[];
    assets: string[];
}

const EarningsPage = () => {
    const authHttp = useAuthHttp();
    const [period, setPeriod] = useState<Date[]>([]);
    const [filters, setFilters] = useState<IEarningsPageFilters>({
        group: [],
        subGroup: [],
        assets: []
    });
    const [groupLabel, setGroupLabel] = useState<string>("");
    const [subGroupLabel, setSubGroupLabel] = useState<string>("");
    const [assetsLabel, setAssetsLabel] = useState<string>("");

    const earningsQuery = useQuery<IEarningsResponse>({
        queryKey: [
            "earnings",
            period.length == 2 ? moment(period[0]).format("YYYY-MM-DD") : "",
            period.length == 2 ? moment(period[1]).format("YYYY-MM-DD") : "",
            filters.group.join(","),
            filters.subGroup.join(","),
            filters.assets.join(",")
        ],
        queryFn: async () => {
            const searchParams = new URLSearchParams();
            if (period.length == 2) {
                searchParams.append("start_date", moment(period[0]).format("YYYY-MM-DD"));
                searchParams.append("end_date", moment(period[1]).format("YYYY-MM-DD"));
            }

            searchParams.append("asset_group_desc", filters.group.join(","));
            searchParams.append("asset_group_id", filters.subGroup.join(","));
            searchParams.append("asset_user_id_list", filters.assets.join(","));
            return authHttp.doGet<IEarningsResponse>(`analysis/payout?${searchParams.toString()}`);
        }
    });

    const assetListTypeQuery = useQuery({
        queryKey: ["assetListType"],
        queryFn: async () =>
            await authHttp.doGet<IAssetTypesResponse>('asset/list_types')
    });

    return (
        <AnalysisContainer
            loading={earningsQuery.isLoading}
            period={period}
            setPeriod={setPeriod}
            showExport={{ filename: "proventos" }}
            filters={[
                {
                    name: "Grupos",
                    content: <GroupMultiSelect 
                        value={filters.group} 
                        onChange={(value) => {
                            setFilters(old => ({ ...old, group: value }));
                        }} 
                        onChangeSelectedLabel={setGroupLabel}
                        assets={assetListTypeQuery.data?.asset_list ?? []}
                    />,
                    chip: { label: groupLabel, icon: "pi-tags" }
                },
                {
                    name: "Subgrupos",
                    content: <SubGroupMultiSelect 
                        selectedGroupFamily={filters.group}
                        value={filters.subGroup} 
                        onChange={(value) => {
                            setFilters(old => ({ ...old, subGroup: value }));
                        }} 
                        onChangeSelectedLabel={setSubGroupLabel}
                        assets={assetListTypeQuery.data?.asset_list ?? []}
                    />,
                    chip: { label: subGroupLabel, icon: "pi-sitemap" }
                },
                {
                    name: "Ativos",
                    content: <AssetsMultiSelect
                        period={period}
                        value={filters.assets}
                        onChange={(value) => {
                            setFilters(old => ({ ...old, assets: value }));
                        }}
                        onChangeSelectedLabel={setAssetsLabel}
                    />,
                    chip: { label: assetsLabel, icon: "pi-chart-bar" }
                }
            ]}
        >
            <Container flexColumn fullWidth gap={3}>
                 <Card
                    title="Análise de Proventos"
                    subTitle="Análise de proventos acumulada por ativo"
                    className="w-full"
                >
                    <DataTable
                        value={earningsQuery.data?.payout_analysis}
                    >
                        <Column field="asset_name" header="Ativo" />
                        <Column 
                            field="asset_sub_type" 
                            header="Subgrupo"
                            body={(rowData: PayoutAnalysis) => {
                                const badgeType = rowData.asset_description == "F" 
                                    ? "p-badge-fixed-income"
                                    : rowData.asset_description == "V" || rowData.asset_description == "T" || rowData.asset_description == "U"
                                        ? "p-badge-variable-income"
                                        : rowData.asset_description == "C"
                                            ? "p-badge-cashflow-income"
                                                : rowData.asset_description == "P"
                                                    ? "p-badge-properties"
                                                    : "p-badge-vehicles";

                                return (
                                    <Badge value={rowData.asset_sub_type} className={badgeType} />
                                )
                            }}
                        />
                        <Column 
                            field="asset_payout_details.asset_quantity" 
                            header="Quantidade" 
                            body={(rowData: PayoutAnalysis) => {
                                return (
                                    <>{Number.isInteger(rowData.asset_payout_details.asset_quantity) ? rowData.asset_payout_details.asset_quantity : rowData.asset_payout_details.asset_quantity.toFixed(2)}</>
                                )
                            }}
                        />
                        <Column 
                            field="asset_payout_details.asset_average_price" 
                            header="Preço Médio" 
                            body={(rowData: PayoutAnalysis) => {
                                return (
                                    <Currency value={rowData.asset_payout_details.asset_average_price}>
                                        {(value) => value}
                                    </Currency>
                                )
                            }}
                            style={{ minWidth: "150px" }}
                        />

                        <Column 
                            field="asset_payout_details.asset_actual_price" 
                            header="Preço Atual" 
                            body={(rowData: PayoutAnalysis) => {
                                return (
                                    <Currency value={rowData.asset_payout_details.asset_actual_price}>
                                        {(value) => value}
                                    </Currency>
                                )
                            }}
                            style={{ minWidth: "150px" }}
                        />

                        <Column 
                            field="asset_payout_details.asset_total_earnings" 
                            header="Proventos Acumulados" 
                            body={(rowData: PayoutAnalysis) => {
                                return (
                                    <Currency value={rowData.asset_payout_details.asset_total_earnings}>
                                        {(value) => value}
                                    </Currency>
                                )
                            }}
                            style={{ minWidth: "220px" }}
                        />

                        <Column 
                            field="asset_payout_details.asset_percent_yield" 
                            header="DY Período" 
                            body={(rowData: PayoutAnalysis) => {
                                return (
                                    <PercentageText color="700" fontWeight="normal">
                                        {rowData.asset_payout_details.asset_percent_yield * 100}
                                    </PercentageText>
                                )
                            }}
                            style={{ minWidth: "160px" }}
                        />

                        <Column 
                            field="asset_payout_details.asset_total_result" 
                            header="Resultado + Proventos" 
                            body={(rowData: PayoutAnalysis) => {
                                return (
                                    <Currency value={rowData.asset_payout_details.asset_total_result}>
                                        {(value) => value}
                                    </Currency>
                                )
                            }}
                            style={{ minWidth: "220px" }}
                        />

                        <Column 
                            field="asset_payout_details.asset_percent_result" 
                            header="Rentabilidade" 
                            body={(rowData: PayoutAnalysis) => {
                                return (
                                    <PercentageText color="700" fontWeight="normal">
                                        {rowData.asset_payout_details.asset_percent_result}
                                    </PercentageText>
                                )
                            }}
                            style={{ minWidth: "130px" }}
                        />
                    </DataTable>
                </Card>
            </Container>
        </AnalysisContainer>
    );
}

export default EarningsPage;
